import { useRef, useState, useCallback } from "react";
import { MaskText, Reveal } from "./Reveal";

export default function BeforeAfter() {
  const [pct, setPct] = useState(50);
  const wrap = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const el = wrap.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPct(Math.max(2, Math.min(98, p)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    update(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    update(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onUp = () => (dragging.current = false);

  return (
    <section className="py-24 sm:py-40 bg-[var(--bone)]" data-testid="before-after">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="overline text-[var(--terra)] mb-6">The hydroquinone edit</p>
            <MaskText
              as="h2"
              lines={["See the fade.", "Drag to reveal."]}
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter"
            />
            <Reveal delay={0.2}>
              <p className="font-body text-lg text-[var(--ink-soft)] leading-relaxed mt-8 max-w-md">
                Hydroquinone 4% and Skinshine are formulated to lift stubborn
                hyperpigmentation and melasma — especially on deeper skin tones,
                where dark spots linger longest.
              </p>
              <div className="mt-8 flex gap-8">
                <div>
                  <div className="font-display text-4xl">8 wks</div>
                  <div className="overline text-[var(--ink-soft)] mt-1">Visible fade</div>
                </div>
                <div className="w-px bg-[var(--line)]" />
                <div>
                  <div className="font-display text-4xl">$24.99</div>
                  <div className="overline text-[var(--ink-soft)] mt-1">Below clinic price</div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div
              ref={wrap}
              onMouseDown={onDown}
              onMouseMove={onMove}
              onMouseUp={onUp}
              onMouseLeave={onUp}
              onTouchStart={onDown}
              onTouchMove={onMove}
              onTouchEnd={onUp}
              className="relative aspect-[4/5] w-full overflow-hidden select-none border border-[var(--line)]"
              data-cursor="hover"
              data-testid="ba-slider"
            >
              {/* AFTER (clean, even, glowing) */}
              <img
                src="/models/after.jpg"
                alt="After hydroquinone treatment — even, glowing skin"
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
              {/* BEFORE (hyperpigmentation, uneven tone) */}
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${pct}%` }}
              >
                <img
                  src="/models/before.jpg"
                  alt="Before treatment — visible dark spots and uneven tone"
                  className="absolute inset-0 h-full object-cover"
                  style={{
                    width: wrap.current ? wrap.current.offsetWidth : "100%",
                    maxWidth: "none",
                  }}
                  draggable={false}
                />
                <span className="absolute top-4 left-4 overline glass px-3 py-1.5 rounded-full">
                  Before
                </span>
              </div>
              <span className="absolute top-4 right-4 overline glass px-3 py-1.5 rounded-full">
                After
              </span>

              {/* handle */}
              <div
                className="absolute top-0 bottom-0 w-px bg-white/90"
                style={{ left: `${pct}%` }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full glass flex items-center justify-center">
                  <span className="font-mono text-xs">↔</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
